import React, { useState, useEffect, useRef } from 'react';
import { Individual, Gender } from '../models/types';
import { DataService } from '../services/DataService';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:3003';

interface IndividualFormProps {
  individual?: Individual;
  onSave: () => void;
  onCancel: () => void;
}

const IndividualForm: React.FC<IndividualFormProps> = ({ individual, onSave, onCancel }) => {
  const isEditMode = !!individual;
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<Omit<Individual, 'id'>>({
    firstName: '',
    lastName: '',
    middleName: '',
    gender: Gender.MALE,
    birthDate: '',
    deathDate: '',
    birthPlace: '',
    deathPlace: '',
    occupation: '',
    education: '',
    photos: [],
    notes: '',
    tags: [],
    isDeceased: false
  });
  
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
  
  useEffect(() => {
    if (individual) {
      // If editing an existing individual, populate the form
      const { id, ...individualData } = individual;
      setFormData(individualData);
      
      // Set profile photo preview if exists
      if (individualData.photos && individualData.photos.length > 0) {
        const photoPath = individualData.photos[0];
        const previewUrl = photoPath.startsWith('http') ? photoPath : `${API_BASE_URL}${photoPath.startsWith('/') ? '' : '/'}${photoPath}`;
        console.log('[PHOTO PREVIEW - edit] previewUrl:', previewUrl);
        setProfilePhotoPreview(previewUrl);
      }
    }
  }, [individual]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // NEW: Upload photo to backend and return file path
  const uploadPhoto = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('photo', file);
    try {
      const response = await fetch('http://localhost:3003/api/uploads/photo', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      return data.path as string;
    } catch (e) {
      alert(t('Failed to upload photo.'));
      return null;
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // File size validation (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('Photo is too large. Maximum size is 5MB.'));
      return;
    }

    // Upload to backend and get file path
    const photoPath = await uploadPhoto(file);
    if (photoPath) {
      setFormData(prev => ({
        ...prev,
        photos: [photoPath]
      }));
      const previewUrl = photoPath.startsWith('http') ? photoPath : `${API_BASE_URL}${photoPath.startsWith('/') ? '' : '/'}${photoPath}`;
      console.log('[PHOTO PREVIEW - upload] previewUrl:', previewUrl);
      setProfilePhotoPreview(previewUrl);
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhotoPreview(null);
    setFormData(prev => ({
      ...prev,
      photos: []
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName) {
      alert(t('First name and last name are required'));
      return;
    }

    // Normalize date fields to YYYY-MM-DD before sending
    const normalizedFormData = {
      ...formData,
      birthDate: formData.birthDate ? formData.birthDate.slice(0, 10) : '',
      deathDate: formData.deathDate ? formData.deathDate.slice(0, 10) : '',
    };

    if (isEditMode && individual) {
      // Update existing individual
      console.log('[UPDATE] Sending to backend:', JSON.stringify(normalizedFormData, null, 2));
      DataService.updateIndividual(individual.id, normalizedFormData);
    } else {
      // Add new individual
      console.log('[CREATE] Sending to backend:', JSON.stringify(normalizedFormData, null, 2));
      DataService.addIndividual(normalizedFormData);
    }

    onSave();
  };
  
  return (
    <div className="individual-form-container">
      <h2>{isEditMode ? t('Edit Individual') : t('Add New Individual')}</h2>
      
      <div className="photo-upload-section">
        <div className="profile-photo-container">
          {profilePhotoPreview ? (
            <div className="profile-photo-preview">
              <img src={profilePhotoPreview} alt={t('Profile')} style={{ maxWidth: '100%', maxHeight: '200px' }} />
              <button 
                type="button" 
                className="remove-photo-btn"
                onClick={handleRemovePhoto}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="profile-photo-placeholder">
              <span role="img" aria-label="profile">👤</span>
            </div>
          )}
        </div>
        
        <div className="photo-upload-controls">
          <input
            type="file"
            id="photoUpload"
            ref={fileInputRef}
            accept="image/*"
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
          />
          <button 
            type="button" 
            className="upload-photo-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            {profilePhotoPreview ? t('Change Photo') : t('Add Profile Photo')}
          </button>
          <span className="photo-help-text">{t('Max size: 5MB')}</span>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="individual-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">{t('First Name*')}</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="middleName">{t('Middle Name')}</label>
            <input
              type="text"
              id="middleName"
              name="middleName"
              value={formData.middleName || ''}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="lastName">{t('Last Name*')}</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="gender">{t('Gender')}</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <option value={Gender.MALE}>{t('Male')}</option>
              <option value={Gender.FEMALE}>{t('Female')}</option>
              <option value={Gender.NON_BINARY}>{t('Non-Binary')}</option>
              <option value={Gender.CUSTOM}>{t('Custom')}</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="birthDate">{t('Birth Date')}</label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={formData.birthDate ? formData.birthDate.slice(0, 10) : ''}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group checkbox-group">
            <label htmlFor="isDeceased">
              <input
                type="checkbox"
                id="isDeceased"
                name="isDeceased"
                checked={formData.isDeceased || false}
                onChange={handleChange}
              />
              {t('Deceased')}
            </label>
          </div>
        </div>
        
        {formData.isDeceased && (
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="deathDate">{t('Death Date')}</label>
              <input
                type="date"
                id="deathDate"
                name="deathDate"
                value={formData.deathDate ? formData.deathDate.slice(0, 10) : ''}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="birthPlace">{t('Birth Place')}</label>
            <input
              type="text"
              id="birthPlace"
              name="birthPlace"
              value={formData.birthPlace || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="occupation">{t('Occupation')}</label>
            <input
              type="text"
              id="occupation"
              name="occupation"
              value={formData.occupation || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="education">{t('Education')}</label>
            <input
              type="text"
              id="education"
              name="education"
              value={formData.education || ''}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="notes">{t('Notes')}</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              rows={3}
            />
          </div>
        </div>
        
        <div className="form-actions">
          <button type="submit" className="submit-btn">{isEditMode ? t('Update') : t('Add')}</button>
          <button type="button" className="cancel-btn" onClick={onCancel}>{t('Cancel')}</button>
        </div>
      </form>
    </div>
  );
};

export default IndividualForm;